import { ReactNode, useState } from "react";
import { useCaptureText } from "../../Helper/CaptureStrings";
import "./MessageVideoPlayer.scss";

export interface VideoAttachment {
  url: string;
  name?: string;
  type?: string;
  posterUrl?: string;
}

const VIDEO_EXTENSION_PATTERN = /\.(mp4|m4v|mov|webm)$/i;

export const isVideoAttachment = (attachment?: {
  url?: string;
  name?: string;
  type?: string;
}): boolean => {
  if (!attachment?.url) {
    return false;
  }
  if ((attachment.type ?? "").toLowerCase().startsWith("video/")) {
    return true;
  }
  if (VIDEO_EXTENSION_PATTERN.test(attachment.name ?? "")) {
    return true;
  }
  try {
    return VIDEO_EXTENSION_PATTERN.test(new URL(attachment.url).pathname);
  } catch (_) {
    return false;
  }
};

// Without a poster, iOS paints nothing until playback starts; a media fragment
// makes it seek to (and show) an early frame instead. Only for http(s) —
// blob:/data: sources are local and already show their first frame.
const withFirstFrameHint = (attachment: VideoAttachment) => {
  const { url } = attachment;
  if (attachment.posterUrl || url.includes("#") || !/^https?:/i.test(url)) {
    return url;
  }
  return `${url}#t=0.1`;
};

/**
 * Inline player for a video attachment (screen recordings sent for a capture
 * request, or any mp4 / mov / webm): plays in place (`playsInline`), fetches
 * metadata only until played, and falls back to the regular attachment link
 * when the browser can't play the format (e.g. HEVC .mov on Chrome).
 */
export const MessageVideoPlayer = ({
  attachment,
  fallback,
  onLoaded,
}: {
  attachment: VideoAttachment;
  /** Rendered instead when this browser can't play the file. */
  fallback: ReactNode;
  onLoaded?: () => void;
}) => {
  const [isUnplayable, setIsUnplayable] = useState(false);
  const t = useCaptureText();
  if (isUnplayable) {
    return <>{fallback}</>;
  }
  return (
    <div className="message-video">
      <video
        className="message-video-player"
        src={withFirstFrameHint(attachment)}
        poster={attachment.posterUrl}
        controls
        playsInline
        preload="metadata"
        aria-label={attachment.name || t("captureVideo")}
        onLoadedMetadata={() => onLoaded?.()}
        onError={() => setIsUnplayable(true)}
      />
    </div>
  );
};
